<template>
    <Loading :loading="loading" :inset="true" class="my-4">
        <form v-if="!loading && user" @submit.prevent="update()">
            <SectionWrapper borderPosition="bottom" id="account">
                <template #title>
                    {{ $t('contact_information') }}
                </template>

                <template #content>
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div class="col-span-2 mb-3" v-if="hasCustomer">
                            <Toggle v-if="edit_profile" v-model:active="active_type" :choices="types" color="#031837" colorGradient="#031837" class="h-full mt-0">
                                <template #choice="{ data }">
                                    <div class="flex flex-col items-center justify-center h-full">
                                        <span>
                                            {{ $translate(data.title) }}
                                        </span>
                                    </div>
                                </template>
                            </Toggle>
                        </div>

                        <Select
                            name="salutation"
                            :noValueAllowed="true"
                            :formComponentId="'user_' + this.$.uid"
                            :label="$t('profile_salutation_')"
                            v-model:selected="user.salutation"
                            :options="salutations"
                            :multiple="false"
                            class="col-span-2"
                        >
                        </Select>

                        <Input
                            type="text"
                            name="first_name"
                            :formComponentId="'user_' + this.$.uid"
                            :label="$t('first_name') + '*'"
                            v-model:value="user.first_name"
                            autocomplete="first-name"
                            class="col-span-2 lg:col-span-1"
                        />

                        <Input
                            type="text"
                            name="last_name"
                            :formComponentId="'user_' + this.$.uid"
                            :label="$t('last_name') + '*'"
                            v-model:value="user.last_name"
                            autocomplete="last-name"
                            class="col-span-2 lg:col-span-1"
                        />

                        <Input
                            type="text"
                            name="email"
                            :formComponentId="'user_' + this.$.uid"
                            :label="$t('mail') + '*'"
                            v-model:value="user.email"
                            autocomplete="mail"
                            class="col-span-2"
                        />

                        <TransitionExpand v-show="showConfirmEmail">
                            <Input
                                type="text"
                                name="email_confirmation"
                                :formComponentId="'user_' + this.$.uid"
                                :label="$t('confirm_mail') + '*'"
                                v-model:value="user.email_confirmation"
                                :preventinsert="true"
                                autocomplete="new-password"
                                class="col-span-2"
                            />
                        </TransitionExpand>

                        <Input
                            type="text"
                            name="birthday"
                            :formComponentId="'user_' + this.$.uid"
                            :label="`${$t('acount_birthday')}`"
                            :mask="language === 'de' ? '##.##.####' : '####-##-##'"
                            v-model:value="birthday_readable"
                            class="col-span-2"
                        />

                        <Input
                            type="text"
                            name="phone"
                            :formComponentId="'customer_' + this.$.uid"
                            :label="$t('profile_phone')"
                            v-model:value="customer.phone"
                            autocomplete="phone"
                            :mask="region === 'de' ? '+## (###) ### #####' : '+## (#) ### ### ## ###'"
                            class="col-span-2 lg:col-span-1"
                        >
                        </Input>
                        <Input
                            type="text"
                            name="mobile"
                            :formComponentId="'customer_' + this.$.uid"
                            :label="$t('profile_mobile')"
                            v-model:value="customer.mobile"
                            autocomplete="mobile"
                            :mask="region === 'de' ? '+## (###) ### #####' : '+## (#) ### ### ## ###'"
                            class="col-span-2 lg:col-span-1"
                        >
                        </Input>
                    </div>
                </template>
            </SectionWrapper>
        </form>
        <form v-if="!loading && hasCustomer && customer" @submit.prevent="update()">
            <SectionWrapper borderPosition="bottom" id="address">
                <template #title>
                    {{ $t('profile_adress') }}
                </template>
                <template #content>
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <Input
                            type="text"
                            name="street"
                            :formComponentId="'customer_' + this.$.uid"
                            :label="$t('profile_street and house number') + '*'"
                            v-model:value="customer.street"
                            autocomplete="street"
                            class="col-span-2 mt-0"
                        >
                        </Input>
                        <Input
                            type="text"
                            name="zipcode"
                            :formComponentId="'customer_' + this.$.uid"
                            :label="$t('profile_zipcode') + '*'"
                            v-model:value="customer.zipcode"
                            autocomplete="zipcode"
                            class="col-span-2 lg:col-span-1"
                        >
                        </Input>
                        <Input
                            type="text"
                            name="location"
                            :formComponentId="'customer_' + this.$.uid"
                            :label="$t('profile_location') + '*'"
                            v-model:value="customer.location"
                            autocomplete="location"
                            class="col-span-2 lg:col-span-1"
                        >
                        </Input>
                        <Select
                            name="country_id"
                            :noValueAllowed="false"
                            v-if="active_user_language"
                            :formComponentId="'customer_' + this.$.uid"
                            :label="$t('profile_country') + '*'"
                            :selected="active_user_language"
                            @update:selected="customer.country_id = $event"
                            :options="filteredCountries"
                            endpoint="/api/v1/countries/autocomplete"
                            :language="getActiveLanguage"
                            :multiple="false"
                            class="col-span-2"
                        >
                        </Select>
                    </div>
                </template>
            </SectionWrapper>

            <TransitionExpand v-show="customer.type === 'business'">
                <SectionWrapper borderPosition="bottom" id="company" v-if="customer.type === 'business'">
                    <template #title>
                        {{ $t('plain_company') }}
                    </template>

                    <template #content>
                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <Input
                                type="text"
                                name="company"
                                :formComponentId="'customer_' + this.$.uid"
                                :label="$t('company_name') + '*'"
                                v-model:value="customer.company"
                                autocomplete="name"
                                class="col-span-2 mt-0"
                            >
                            </Input>
                            <Input
                                type="text"
                                name="tax_number"
                                :formComponentId="'customer_' + this.$.uid"
                                :label="$t('tax_number') + '*'"
                                v-model:value="customer.tax_number"
                                autocomplete="none"
                                class="col-span-2 lg:col-span-1"
                            >
                            </Input>
                            <Input
                                type="text"
                                name="email_invoice"
                                :formComponentId="'customer_' + this.$.uid"
                                :label="$t('email_invoice')"
                                v-model:value="customer.email_invoice"
                                autocomplete="email"
                                class="col-span-2 lg:col-span-1"
                            >
                            </Input>
                        </div>
                    </template>
                </SectionWrapper>
            </TransitionExpand>
        </form>

        <div class="flex justify-between">
            <FormButton type="button" @click="abort()" class="w-auto" styling="primary flex items-center gap-3" :value="$t('plain_abort')" :disabled="loading">
                <transition name="fade">
                    <FontAwesomeIcon v-show="loading" :icon="['fas', 'circle-notch']" class="animate-spin text-white transition-all ease-in-out duration-300"></FontAwesomeIcon>
                </transition>
            </FormButton>
            <FormButton
                type="button"
                @click="update()"
                :formComponentId="'customer_' + this.$.uid"
                class="w-auto"
                styling="primary flex items-center gap-3"
                :value="$t('plain_update')"
                :disabled="loading"
            >
                <transition name="fade">
                    <FontAwesomeIcon v-show="loading" :icon="['fas', 'circle-notch']" class="animate-spin text-white transition-all ease-in-out duration-300"></FontAwesomeIcon>
                </transition>
            </FormButton>
        </div>

        <hr class="my-8 w-full border-b-0.5 border-gray-300" />

        <FormButton
            v-if="$route.path === '/profile'"
            type="button"
            @click="removeAccountModalOpen = true"
            class="w-auto"
            styling="secondary !border-red flex items-center gap-3"
            :value="$t('remove_account')"
        >
        </FormButton>

        <RemoveAccountModal v-model:open="removeAccountModalOpen" />
    </Loading>
</template>

<script>
import { mapGetters } from 'vuex';
import { getActiveLanguage } from 'laravel-vue-i18n';
import i18next from 'i18next';
import { formatDateByRegion } from '../../helpers.js';

export default {
    props: ['edit_profile'],

    data: () => ({
        loading: true,
        removeAccountModalOpen: false,
        active_type: null,
        getActiveLanguage: getActiveLanguage(),
        filteredCountries: [],
        initialEmail: null,
        showConfirmEmail: false,
        birthday_readable: null,
        region: window.meta?.region_configuration.region
    }),

    mounted() {
        this.loading = true;
        let promisses = [];

        this.$store.dispatch('user/user').then(() => {
            if (this.user?.customer_id) {
                promisses.push(this.$store.dispatch('customers/show', this.user.customer_id));
            }

            if (this.user?.birthday_at) {
                this.birthday_readable = formatDateByRegion(this.$moment(this.user.birthday_at));
            }

            promisses.push(this.$store.dispatch('countries/index'));

            Promise.all(promisses).then(() => {
                // set initial email
                if (this.user) {
                    this.initialEmail = this.user.email;
                    this.user.email = this.user.email;
                    this.user.email_confirmation = this.user.email;
                    this.showConfirmEmail = false;
                    // set the customer type
                    if (this.user?.customer.type) {
                        this.active_type = this.types.findIndex((customerType) => customerType.value === this.user?.customer.type);
                    } else {
                        this.active_type = 0;
                    }
                } else {
                    throw new Error('User not given');
                }

                this.loading = false;
            });
        });
    },

    methods: {
        async update() {
            // unset the Errors in Store
            this.$store.commit('error/unsetByComponentId', 'user_' + this.$.uid);
            this.$store.commit('error/unsetByComponentId', 'customer_' + this.$.uid);

            return await this.updateProfile().then(() => {
                this.$emit('close');
            });
        },

        async abort() {
            this.loading = true;
            await this.$store.dispatch('user/user');
            await this.$store.dispatch('customers/show', this.user.customer_id);
            this.loading = false;
            this.$emit('close');
        },

        async updateProfile() {
            let promisses = [];

            this.loading = true;

            if (this.hasCustomer) {
                promisses.push(this.updateCustomer());
            }

            // set the birthday
            if (this.birthday_readable) {
                let isoDate;
                if (this.region === 'de') {
                    const [day, month, year] = this.birthday_readable.split('.');
                    isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                } else {
                    // already ISO format: YYYY-MM-DD
                    isoDate = this.birthday_readable;
                }
                this.user.birthday_at = isoDate;
            } else {
                this.user.birthday_at = null;
            }

            // if user salutation is empty send an empty string
            if (!this.user.salutation) {
                this.user.salutation = '';
            }

            promisses.push(this.updateUser());

            return await Promise.all(promisses)
                .then(async () => {
                    this.loading = false;
                    await this.$store.dispatch('user/user');
                    this.$toast.success(i18next.t('account_succesfully_updated'));
                    return;
                })
                .catch((error) => {
                    this.loading = false;
                    this.$toast.error(i18next.t('account_update_failed'));
                    return Promise.reject(error);
                })
                .catch((error) => {
                    this.loading = false;
                    this.$toast.error(trans('There were Errors updating your Profile!'));
                    return Promise.reject(error);
                });
        },

        async updateCustomer() {
            let data = this.customer;
            data.config = { componentId: 'customer_' + this.$.uid };

            if (this.customer.type === 'private') {
                data.email_invoice = null;
                data.tax_number = null;
                data.company = null;
            }

            return await this.$store
                .dispatch('customers/update', {
                    ...data,
                    type: this.customer.type || 'private'
                })
                .catch((error) => {
                    return Promise.reject(error);
                });
        },

        async updateUser() {
            let data = this.user;
            data.config = { componentId: 'user_' + this.$.uid };

            if (!data.email_confirmation) {
                data.email_confirmation = data.email;
            }

            return await this.$store.dispatch('user/update', data).catch((error) => {
                this.$toast(error.response.data.message);
                return Promise.reject(error);
            });
        }
    },

    watch: {
        countries() {
            this.countries.forEach((country) => {
                let newCountry = {
                    label: country.name,
                    value: country.id
                };
                this.filteredCountries.push(newCountry);
            });
        },
        active_type: function () {
            if (this.hasCustomer) {
                this.customer.type = this.types[this.active_type].value;
            }
        },
        'user.email': function (newValue, oldValue) {
            if (newValue != this.initialEmail && newValue != '' && this.user) {
                this.user.email_confirmation = null;
                this.showConfirmEmail = true;
            } else {
                this.user.email_confirmation = this?.initialEmail || null;
                this.showConfirmEmail = false;
            }
        }
    },

    computed: {
        ...mapGetters('user', {
            user: 'get',
            hasCustomer: 'hasCustomer'
        }),
        ...mapGetters('customers', {
            customer: 'get'
        }),
        ...mapGetters('countries', {
            countries: 'get'
        }),
        language() {
            return window.meta.region_configuration.region;
        },
        active_user_language: function () {
            if (this.countries.length) {
                if (this.customer.country_id) {
                    return this.customer.country_id;
                }

                let country = this.countries.find((country) => country.language.iso_3166_2 === this.getActiveLanguage).id;

                this.customer.country_id = country;
                return this.customer.country_id;
            }
        },
        salutations() {
            return [
                {
                    value: 'male',
                    label: i18next.t('profile_salutation_male')
                },
                {
                    value: 'female',
                    label: i18next.t('profile_salutation_female')
                }
            ];
        },
        types() {
            return [
                {
                    value: 'private',
                    title: i18next.t('plain_private')
                },
                {
                    value: 'business',
                    title: i18next.t('plain_company')
                }
            ];
        }
    }
};
</script>
